const CART_COUPON_STORAGE_KEY = 'cart_applied_coupon_code';
const CART_COUPON_STORAGE_KEY_MULTI = 'cart_applied_coupon_codes';
const CART_COUPON_CHECKOUT_ONLY_KEY = 'cart_coupon_checkout_only';
const MR_DRAWER_CROSS_SELL_ID = 'mr-div-embedded-cp-any-drawer';
const CART_DRAWER_COUPON_SLOT_SELECTOR =
  '#cart-drawer-coupon-slot, .cart-drawer__coupon-slot';
const DEFAULT_MAX_COUPONS = 2;
const DEFAULT_STORE_ID = 1818;
const EXPLAIN_TIMEOUT_MS = 4000;
const DEFAULT_EXPLAIN_ENDPOINT =
  'https://www.chicme.info/ppzn/api/shopify/cart/coupon/unavailable/explain';
const ADD_MORE_REASONS = new Set([
  'MIN_QUANTITY',
  'PRODUCT_NOT_ELIGIBLE',
  'COLLECTION_NOT_ELIGIBLE',
]);

const STATUS_ICON_SVG =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="6" cy="6" r="6" fill="#22A06B"/>' +
  '<path d="M3.5 6.2L5.2 7.9L8.5 4.3" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const REMOVE_ICON_SVG =
  '<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
  '</svg>';

function ensureCartDrawerCouponAboveMrWidget() {
  const inner = document.querySelector('cart-drawer .drawer__inner');
  if (!inner) return;

  const slot = inner.querySelector(CART_DRAWER_COUPON_SLOT_SELECTOR);
  const mr = inner.querySelector(`#${MR_DRAWER_CROSS_SELL_ID}`);
  if (!slot || !mr || slot === mr) return;

  if (slot.nextElementSibling !== mr) {
    mr.parentNode.insertBefore(slot, mr);
  }
}

function initCartDrawerCouponPositionObserver() {
  if (window.__geekoCartDrawerCouponMrObserver) return;
  window.__geekoCartDrawerCouponMrObserver = true;

  const start = () => {
    const inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return;

    ensureCartDrawerCouponAboveMrWidget();

    const obs = new MutationObserver(() => {
      ensureCartDrawerCouponAboveMrWidget();
    });
    obs.observe(inner, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

function getCartUpdateJsUrl() {
  if (typeof routes !== 'undefined' && routes.cart_update_js_url) {
    return routes.cart_update_js_url;
  }
  const base =
    (typeof routes !== 'undefined' && routes.cart_update_url) || '/cart/update';
  return base.endsWith('.js') ? base : `${base}.js`;
}

class CartCoupon extends HTMLElement {
  constructor() {
    super();
    this._onApply = this._onApply.bind(this);
    this._onListClick = this._onListClick.bind(this);
    this._onNudgeCtaClick = this._onNudgeCtaClick.bind(this);
    this._appliedItems = [];
    this._hasError = false;
  }

  connectedCallback() {
    this._maxCoupons = Number(this.dataset.maxCoupons) || DEFAULT_MAX_COUPONS;
    this._cacheElements();
    this._syncFromUrl();
    this._clearCheckoutOnlyLegacy();
    this._hydrateFromDom();
    this._bindEvents();
    this._updateLimitUi();
    this._updateHint();
    this._updateExpiringBanner();
    if (this.closest('cart-drawer')) {
      ensureCartDrawerCouponAboveMrWidget();
    }
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  _cacheElements() {
    this._input = this.querySelector('.cart-coupon__input');
    this._applyBtn = this.querySelector('.cart-coupon__btn--apply');
    this._hintEl = this.querySelector('[data-role="hint"]');
    this._errorEl = this.querySelector('[data-role="error"]');
    this._nudgeEl = this.querySelector('[data-role="nudge"]');
    this._nudgeTextEl = this.querySelector('[data-role="nudge-text"]');
    this._nudgeCtaEl = this.querySelector('[data-role="nudge-cta"]');
    this._listEl = this.querySelector('[data-role="applied-list"]');
    this._expiringEl = this.querySelector('[data-role="expiring"]');
  }

  _bindEvents() {
    this._applyBtn?.addEventListener('click', this._onApply);
    this._listEl?.addEventListener('click', this._onListClick);
    this._nudgeCtaEl?.addEventListener('click', this._onNudgeCtaClick);
    this._input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!this.classList.contains('cart-coupon--at-limit')) this._onApply();
      }
    });
  }

  _unbindEvents() {
    this._applyBtn?.removeEventListener('click', this._onApply);
    this._listEl?.removeEventListener('click', this._onListClick);
    this._nudgeCtaEl?.removeEventListener('click', this._onNudgeCtaClick);
  }

  _getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  _normalizeCode(code) {
    return (code || '').trim().toUpperCase();
  }

  _syncFromUrl() {
    const urlCode = new URLSearchParams(window.location.search).get('discount');
    if (!urlCode) return;
    const codes = urlCode
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, this._maxCoupons);
    if (codes.length) this._saveCodes(codes);
  }

  _clearCheckoutOnlyLegacy() {
    try {
      sessionStorage.removeItem(CART_COUPON_CHECKOUT_ONLY_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  _readStoredCodes() {
    try {
      const multi = sessionStorage.getItem(CART_COUPON_STORAGE_KEY_MULTI);
      if (multi) {
        const parsed = JSON.parse(multi);
        if (Array.isArray(parsed)) {
          return parsed
            .map((c) => String(c || '').trim())
            .filter(Boolean)
            .slice(0, this._maxCoupons);
        }
      }
      const legacy = sessionStorage.getItem(CART_COUPON_STORAGE_KEY);
      if (legacy) return [legacy.trim()].filter(Boolean);
    } catch (e) {
      /* ignore */
    }
    return [];
  }

  _saveCodes(codes) {
    const cleaned = (codes || [])
      .map((c) => String(c || '').trim())
      .filter(Boolean)
      .slice(0, this._maxCoupons);
    try {
      sessionStorage.setItem(CART_COUPON_STORAGE_KEY_MULTI, JSON.stringify(cleaned));
      if (cleaned[0]) {
        sessionStorage.setItem(CART_COUPON_STORAGE_KEY, cleaned[0]);
      } else {
        sessionStorage.removeItem(CART_COUPON_STORAGE_KEY);
      }
    } catch (e) {
      /* ignore */
    }
  }

  _hydrateFromDom() {
    const items = [];
    this._listEl?.querySelectorAll('.cart-coupon__item').forEach((el) => {
      const code = el.dataset.code || el.querySelector('.cart-coupon__code')?.textContent || '';
      const amountText = el.querySelector('.cart-coupon__amount')?.textContent?.trim() || '';
      if (code.trim()) {
        items.push({ code: code.trim(), amountText, amount: null });
      }
    });
    this._appliedItems = items;
    if (items.length) {
      this._saveCodes(items.map((i) => i.code));
    }
  }

  _getExpiringCodeSet() {
    const raw = this.dataset.expiringCodes || '';
    return new Set(
      raw
        .split(',')
        .map((c) => this._normalizeCode(c))
        .filter(Boolean)
    );
  }

  _formatMoney(cents) {
    if (cents == null || Number.isNaN(Number(cents))) return '';
    let value = Number(cents);
    const format = this.dataset.moneyFormat || '${{amount}}';

    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, format);
    }

    const abs = Math.abs(value);
    const dollars = (abs / 100).toFixed(2);
    const dollarsNoDecimals = String(Math.round(abs / 100));
    const amountWithCommaSeparator = dollars
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const amountWithApostropheSeparator = dollars.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      "'"
    );

    return format
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        amountWithCommaSeparator
      )
      .replace(
        /\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,
        amountWithApostropheSeparator
      )
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollarsNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, dollars);
  }

  _formatDiscountAmount(cents) {
    if (!cents || Number(cents) <= 0) return '';
    const formatted = this._formatMoney(cents);
    if (!formatted) return '';
    return formatted.startsWith('-') ? formatted : `-${formatted}`;
  }

  /** Cart Ajax API nests fields under discount_application; Liquid uses flat objects. */
  _getDiscountApplication(entry) {
    if (!entry || typeof entry !== 'object') return null;
    if (
      entry.discount_application &&
      typeof entry.discount_application === 'object'
    ) {
      return entry.discount_application;
    }
    return entry;
  }

  _getDiscountApplicationType(entry) {
    return this._getDiscountApplication(entry)?.type || '';
  }

  _getDiscountApplicationTitle(entry) {
    return this._getDiscountApplication(entry)?.title || '';
  }

  _getDiscountApplicationAmount(entry) {
    const app = this._getDiscountApplication(entry);
    if (!app) return 0;
    return (
      app.total_allocated_amount ??
      entry.total_allocated_amount ??
      app.amount ??
      entry.amount ??
      0
    );
  }

  /**
   * Collect allocated amount per discount code.
   * Prefer cart.discount_applications (already totals). Do not also sum
   * line_level_discount_allocations for the same code — that double-counts
   * (e.g. -$11.99 shown as -$23.98).
   * Ajax /cart.js often lacks discount_applications; then merge cart-level
   * totals with line-level only for codes not already present.
   */
  _collectDiscountCodeAmounts(cartState) {
    const map = new Map();

    const add = (code, amount) => {
      const trimmed = String(code || '').trim();
      if (!trimmed) return;
      const key = this._normalizeCode(trimmed);
      const prev = map.get(key) || { code: trimmed, amount: 0 };
      prev.amount += Number(amount) || 0;
      if (!prev.code) prev.code = trimmed;
      map.set(key, prev);
    };

    const addFromApp = (app, amountOverride) => {
      if (this._getDiscountApplicationType(app) !== 'discount_code') return;
      add(
        this._getDiscountApplicationTitle(app),
        amountOverride ?? this._getDiscountApplicationAmount(app)
      );
    };

    // Full application list already has total_allocated_amount per code
    if (Array.isArray(cartState?.discount_applications)) {
      for (const app of cartState.discount_applications) addFromApp(app);
      return map;
    }

    // Ajax cart.js fallback: order-wide codes
    if (Array.isArray(cartState?.cart_level_discount_applications)) {
      for (const app of cartState.cart_level_discount_applications) {
        addFromApp(app);
      }
    }

    // Product/collection codes only appear on lines; skip codes already counted
    const items = cartState?.items;
    if (Array.isArray(items)) {
      for (const item of items) {
        const allocations = item.line_level_discount_allocations;
        if (Array.isArray(allocations)) {
          for (const alloc of allocations) {
            const type =
              this._getDiscountApplicationType(alloc) ||
              alloc?.discount_application?.type ||
              '';
            if (type !== 'discount_code') continue;
            const code =
              this._getDiscountApplicationTitle(alloc) ||
              alloc?.discount_application?.title;
            const key = this._normalizeCode(code);
            if (!key || map.has(key)) continue;
            add(code, alloc.amount ?? alloc.total_allocated_amount ?? 0);
          }
        }

        const discounts = item.discounts;
        if (Array.isArray(discounts)) {
          for (const d of discounts) {
            if (!d?.title) continue;
            const key = this._normalizeCode(d.title);
            if (map.has(key)) continue;
            add(d.title, d.amount ?? 0);
          }
        }
      }
    }

    return map;
  }

  /**
   * Prefer cart.discount_codes as source of truth (supports multiple codes).
   * Amounts come from _collectDiscountCodeAmounts (no double-count).
   */
  _extractAppliedItemsFromCart(cartState, preferredCodes = null) {
    const amountMap = this._collectDiscountCodeAmounts(cartState);
    const items = [];
    const seen = new Set();

    const pushCode = (code) => {
      const trimmed = String(code || '').trim();
      if (!trimmed) return false;
      const key = this._normalizeCode(trimmed);
      if (seen.has(key)) return false;
      seen.add(key);
      const amountInfo = amountMap.get(key);
      const amount =
        amountInfo && amountInfo.amount > 0 ? amountInfo.amount : null;
      items.push({
        code: amountInfo?.code || trimmed,
        amount,
        amountText: amount ? this._formatDiscountAmount(amount) : '',
      });
      return true;
    };

    if (Array.isArray(preferredCodes) && preferredCodes.length) {
      for (const code of preferredCodes) {
        if (!this._hasDiscountCodeOnCart(cartState, code)) continue;
        pushCode(code);
        if (items.length >= this._maxCoupons) break;
      }
      if (items.length >= this._maxCoupons) return items;
    }

    const discountCodes = cartState?.discount_codes;
    if (Array.isArray(discountCodes) && discountCodes.length) {
      for (const entry of discountCodes) {
        const code =
          typeof entry === 'string' ? entry : entry?.code || entry?.title || '';
        if (!code) continue;
        if (entry && typeof entry === 'object' && entry.applicable === false) {
          continue;
        }
        pushCode(code);
        if (items.length >= this._maxCoupons) break;
      }
    }

    // Product-level codes may be missing from discount_codes but present in allocations
    for (const [, info] of amountMap) {
      if (items.length >= this._maxCoupons) break;
      pushCode(info.code);
    }

    return items;
  }

  _missingCodesOnCart(cartState, codes) {
    return (codes || []).filter((code) => !this._hasDiscountCodeOnCart(cartState, code));
  }

  async _resolveCartState(parsedState) {
    try {
      const fresh = await this._fetchCartJs();
      if (!fresh) return parsedState;
      return {
        ...parsedState,
        ...fresh,
        sections: parsedState?.sections,
        item_count: parsedState?.item_count ?? fresh.item_count,
      };
    } catch (e) {
      return parsedState;
    }
  }

  async _fetchCartJs() {
    const root = this._getShopifyRoot();
    const url = `${String(root || '/').replace(/\/?$/, '/')}cart.js`;
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return response.json();
  }

  _hasDiscountCodeOnCart(cartState, code) {
    const normalized = this._normalizeCode(code);
    if (!normalized) return false;

    const discountCodes = cartState?.discount_codes;
    if (Array.isArray(discountCodes)) {
      const found = discountCodes.find((entry) => {
        const entryCode =
          typeof entry === 'string' ? entry : entry?.code || entry?.title || '';
        return this._normalizeCode(entryCode) === normalized;
      });
      if (found) {
        if (typeof found === 'object' && found.applicable === false) return false;
        return true;
      }
    }

    const amountMap = this._collectDiscountCodeAmounts(cartState);
    if (amountMap.has(normalized)) return true;

    return false;
  }

  _getDiscountEntry(cartState, code) {
    const discountCodes = cartState?.discount_codes;
    if (!Array.isArray(discountCodes)) return null;

    const normalized = this._normalizeCode(code);
    for (const entry of discountCodes) {
      const entryCode =
        typeof entry === 'string' ? entry : entry?.code || entry?.title || '';
      if (this._normalizeCode(entryCode) === normalized) return entry;
    }
    return null;
  }

  _invalidErrorMessage() {
    return (
      window.cartStrings?.couponErrorInvalid ||
      'This coupon code is invalid. Please check and try again.'
    );
  }

  _pickField(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const key of keys) {
      if (obj[key] != null && obj[key] !== '') return obj[key];
    }
    return undefined;
  }

  _fillTemplate(template, vars) {
    let out = String(template || '');
    Object.keys(vars || {}).forEach((key) => {
      const value = vars[key] == null ? '' : String(vars[key]);
      out = out.split(`[${key}]`).join(value);
      out = out.split(`{{${key}}}`).join(value);
      out = out.split(`{{ ${key} }}`).join(value);
    });
    return out;
  }

  _formatOff(result) {
    const type = String(
      this._pickField(result, 'discount_type', 'discountType') || ''
    ).toLowerCase();
    const value = Number(
      this._pickField(result, 'discount_value', 'discountValue')
    );
    if (!Number.isFinite(value) || value <= 0) return '';
    if (type === 'percentage') return `${value}%`;
    return this._formatMoney(value);
  }

  _getExplainEndpoint() {
    return (
      String(this.dataset.explainEndpoint || '').trim() ||
      DEFAULT_EXPLAIN_ENDPOINT
    );
  }

  _getStoreId() {
    const fromGc = Number(
      window.geekoGiftcardScanPopupConfig?.defaultStoreId
    );
    if (fromGc) return fromGc;
    const fromData = Number(this.dataset.storeId);
    if (fromData) return fromData;
    return DEFAULT_STORE_ID;
  }

  _getCustomerId() {
    const raw = this.dataset.customerId;
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  _buildExplainPayload(couponCode, cartState) {
    const items = Array.isArray(cartState?.items)
      ? cartState.items.map((item) => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          sku: item.sku || '',
          quantity: item.quantity,
          final_line_price: item.final_line_price,
        }))
      : [];

    const payload = {
      shopify_store_account_id: this._getStoreId(),
      coupon_code: couponCode,
      currency: cartState?.currency || 'USD',
      applied_codes: this._appliedItems.map((item) => item.code).filter(Boolean),
      cart: {
        item_count: cartState?.item_count || 0,
        total_price: cartState?.total_price || 0,
        items_subtotal_price: cartState?.items_subtotal_price || 0,
        items,
      },
    };

    const customerId = this._getCustomerId();
    if (customerId) payload.customer_id = customerId;
    return payload;
  }

  async _explainUnavailable(code, cartState) {
    const endpoint = this._getExplainEndpoint();
    if (!endpoint || !code) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), EXPLAIN_TIMEOUT_MS);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(this._buildExplainPayload(code, cartState || {})),
        signal: controller.signal,
      });
      return await response.json();
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  _parseExplainResult(payload) {
    if (!payload || typeof payload !== 'object') return null;

    const code = Number(payload.code);
    if (code === 40402) return { reason_code: 'NOT_FOUND' };

    const result =
      payload.result != null && typeof payload.result === 'object'
        ? payload.result
        : payload.data != null && typeof payload.data === 'object'
          ? payload.data
          : null;
    const success =
      payload.success === true || code === 0 || code === 200;

    if (!success || !result) return null;

    const reason = String(
      this._pickField(result, 'reason_code', 'reasonCode') || ''
    ).toUpperCase();
    if (!reason) return null;

    return { ...result, reason_code: reason };
  }

  _messageForReason(result) {
    const reason = result?.reason_code || '';
    const s = window.cartStrings || {};
    const amount = this._formatMoney(
      this._pickField(result, 'gap_amount', 'gapAmount')
    );
    const count = String(
      this._pickField(result, 'gap_quantity', 'gapQuantity') ?? ''
    );
    const fallbackMessage = this._pickField(
      result,
      'reason_message',
      'reasonMessage'
    );

    switch (reason) {
      case 'NOT_FOUND':
      case 'UNKNOWN':
        return s.couponErrorInvalid || this._invalidErrorMessage();
      case 'EXPIRED':
        return (
          s.couponErrorExpired ||
          'This coupon code has expired. Please try another code.'
        );
      case 'NOT_STARTED':
      case 'SCHEDULED':
        return (
          s.couponErrorNotStarted ||
          'This coupon code is not available yet.'
        );
      case 'DISABLED':
        return (
          s.couponErrorDisabled ||
          'This coupon code is not available right now.'
        );
      case 'COMBINE_CONFLICT':
        return (
          s.couponErrorCombine ||
          'This coupon code cannot be combined with your existing coupon.'
        );
      case 'MIN_SUBTOTAL':
        return this._fillTemplate(
          s.couponErrorMinSubtotal ||
            'Add [amount] of items for Coupon use.',
          { amount }
        );
      case 'MIN_QUANTITY':
        return this._fillTemplate(
          s.couponErrorMinQuantity ||
            'Add [count] more item(s) for Coupon use.',
          { count }
        );
      case 'PRODUCT_NOT_ELIGIBLE':
      case 'COLLECTION_NOT_ELIGIBLE':
        return (
          s.couponErrorNotEligible ||
          'This coupon does not apply to items in your cart.'
        );
      case 'CUSTOMER_NOT_ELIGIBLE':
        return (
          s.couponErrorCustomer ||
          'This coupon is not available for your account.'
        );
      case 'USAGE_LIMIT':
        return (
          s.couponErrorUsageLimit ||
          'This coupon code is no longer available.'
        );
      case 'ONCE_PER_CUSTOMER':
        return (
          s.couponErrorOncePerCustomer ||
          'You have already used this coupon code.'
        );
      default:
        return fallbackMessage || this._invalidErrorMessage();
    }
  }

  _ctaFromResult(result) {
    const cta = result?.cta;
    if (!cta || typeof cta !== 'object') return { type: '', url: '' };
    return {
      type: String(this._pickField(cta, 'type') || '').toUpperCase(),
      url: String(this._pickField(cta, 'url') || '').trim(),
    };
  }

  _showNudge(result) {
    if (!this._nudgeEl) return;

    const reason = result?.reason_code || '';
    const gap = Number(this._pickField(result, 'gap_amount', 'gapAmount'));
    const off = this._formatOff(result);
    const amount = this._formatMoney(gap);
    const cta = this._ctaFromResult(result);

    let text = '';
    let show = false;

    if (reason === 'MIN_SUBTOTAL' && gap > 0 && off) {
      text = this._fillTemplate(
        window.cartStrings?.couponNudgeMinSubtotal ||
          'Add [amount] Get Extra [off] OFF',
        { amount, off }
      );
      show = true;
    } else if (cta.type === 'ADD_MORE' && ADD_MORE_REASONS.has(reason)) {
      show = true;
    }

    if (!show) {
      this._clearNudge();
      return;
    }

    this._nudgeEl.hidden = false;
    if (this._nudgeTextEl) {
      this._nudgeTextEl.textContent = text;
      this._nudgeTextEl.hidden = !text;
    }
    if (this._nudgeCtaEl) {
      this._nudgeCtaEl.textContent =
        window.cartStrings?.couponNudgeAdd || 'Add>';
      this._nudgeCtaEl.href = cta.url || '#';
      this._nudgeCtaEl.hidden = false;
    }
  }

  _clearNudge() {
    if (this._nudgeEl) this._nudgeEl.hidden = true;
    if (this._nudgeTextEl) {
      this._nudgeTextEl.textContent = '';
      this._nudgeTextEl.hidden = false;
    }
    if (this._nudgeCtaEl) {
      this._nudgeCtaEl.href = '#';
    }
  }

  _onNudgeCtaClick(event) {
    const href = this._nudgeCtaEl?.getAttribute('href') || '';
    if (href && href !== '#') return;
    event.preventDefault();
    const mr = document.getElementById(MR_DRAWER_CROSS_SELL_ID);
    mr?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }

  async _explainAndShow(code, cartState) {
    let state = cartState;
    if (!state?.items) {
      try {
        state = (await this._fetchCartJs()) || state;
      } catch (e) {
        /* keep previous state */
      }
    }

    const payload = await this._explainUnavailable(code, state || {});
    const parsed = this._parseExplainResult(payload);
    if (!parsed) {
      this._showError(this._invalidErrorMessage());
      this._clearNudge();
      return;
    }

    this._showError(this._messageForReason(parsed));
    this._clearNudge();
  }

  /** True when Shopify rejected the code (present but not applicable). */
  _isDiscountCodeRejected(cartState, code) {
    const entry = this._getDiscountEntry(cartState, code);
    return Boolean(entry && typeof entry === 'object' && entry.applicable === false);
  }

  async _restorePreviousCodes(previousCodes, sections) {
    if (!previousCodes.length) return null;
    const parsedState = await this._requestCartUpdate({
      discount: previousCodes
        .map((c) => String(c).trim())
        .filter(Boolean)
        .join(','),
      sections,
    });
    return {
      parsedState,
      cartState: await this._resolveCartState(parsedState),
    };
  }

  _decodeHtmlEntities(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  _showError(message) {
    if (!this._errorEl) return;
    let text = message;
    if (message && typeof message === 'object') {
      text = message.description || message.message || message.errors || '';
    }
    text = this._decodeHtmlEntities(String(text || ''));
    this._hasError = Boolean(text);
    this._errorEl.textContent = text || '';
    this._errorEl.hidden = !text;
    this._clearNudge();
    if (text && this._hintEl) this._hintEl.hidden = true;
    if (!text) this._updateHint();
  }

  _clearError() {
    this._hasError = false;
    if (this._errorEl) {
      this._errorEl.textContent = '';
      this._errorEl.hidden = true;
    }
    this._clearNudge();
  }

  _updateHint() {
    if (!this._hintEl) return;
    if (this._hasError) {
      this._hintEl.hidden = true;
      return;
    }
    const atLimit = this._appliedItems.length >= this._maxCoupons;
    this._hintEl.hidden = false;
    this._hintEl.textContent = atLimit
      ? window.cartStrings?.couponHintMax ||
        'Maximum number of coupon codes applied'
      : window.cartStrings?.couponHintLimit ||
        '*Apply up to 2 coupon codes. Auto discounts apply when conditions are met.';
  }

  _updateLimitUi() {
    const atLimit = this._appliedItems.length >= this._maxCoupons;
    this.classList.toggle('cart-coupon--at-limit', atLimit);
    if (this._input) this._input.disabled = atLimit;
    if (this._applyBtn) this._applyBtn.disabled = atLimit;
  }

  _updateExpiringBanner() {
    if (!this._expiringEl) return;
    const expiringSet = this._getExpiringCodeSet();
    const show =
      expiringSet.size > 0 &&
      this._appliedItems.some((item) =>
        expiringSet.has(this._normalizeCode(item.code))
      );
    this._expiringEl.hidden = !show;
  }

  _renderAppliedList() {
    if (!this._listEl) return;

    if (!this._appliedItems.length) {
      this._listEl.innerHTML = '';
      this._listEl.hidden = true;
      return;
    }

    const statusText =
      window.cartStrings?.couponAppliedStatus || 'Coupon applied';
    const removeAria =
      window.cartStrings?.couponRemoveAria || 'Remove coupon';

    this._listEl.hidden = false;
    this._listEl.innerHTML = this._appliedItems
      .map((item) => {
        const code = this._escapeHtml(item.code);
        const amount = item.amountText
          ? `<span class="cart-coupon__amount">${this._escapeHtml(item.amountText)}</span>`
          : '';
        return (
          `<li class="cart-coupon__item" data-code="${code}">` +
          `<div class="cart-coupon__item-main">` +
          `<span class="cart-coupon__code">${code}</span>` +
          `<span class="cart-coupon__status">` +
          `<span class="cart-coupon__status-icon" aria-hidden="true">${STATUS_ICON_SVG}</span>` +
          `${this._escapeHtml(statusText)}` +
          `</span></div>` +
          `<div class="cart-coupon__item-side">` +
          amount +
          `<button type="button" class="cart-coupon__item-remove" data-code="${code}" aria-label="${this._escapeHtml(removeAria)}">` +
          `${REMOVE_ICON_SVG}</button>` +
          `</div></li>`
        );
      })
      .join('');
  }

  _escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _setAppliedItems(items, { clearInput = false } = {}) {
    this._appliedItems = (items || []).slice(0, this._maxCoupons);
    this._saveCodes(this._appliedItems.map((i) => i.code));
    this._renderAppliedList();
    this._updateLimitUi();
    this._updateHint();
    this._updateExpiringBanner();
    if (clearInput && this._input) this._input.value = '';
  }

  _syncAppliedFromCartState(parsedState) {
    if (!parsedState) return;
    const items = this._extractAppliedItemsFromCart(parsedState);
    this._setAppliedItems(items);
  }

  _syncVisibilityFromCart(itemCount) {
    if (typeof itemCount !== 'number') return;
    this.hidden = itemCount === 0;
  }

  _setLoading(loading) {
    this.classList.toggle('cart-coupon--loading', loading);
    if (this._applyBtn && !this.classList.contains('cart-coupon--at-limit')) {
      this._applyBtn.disabled = loading;
    }
    this.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  _getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    if (this.closest('cart-drawer')) {
      sections.unshift(
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.js-contents',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__header',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.cart-drawer__footer',
        }
      );
      return sections;
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (!mainCartItems?.dataset?.id) return [];

    sections.unshift(
      {
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.cart-coupon-slot--inline',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    );

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter?.dataset?.id) {
      sections.push(
        {
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.cart-coupon-slot--sidebar',
        },
        {
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.js-contents-footer',
        },
        {
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.js-contents-shiping',
        }
      );
    }

    return sections;
  }

  _getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  _isCouponSlotSelector(selector) {
    return String(selector || '').includes('cart-coupon-slot');
  }

  _getLiveCoupons() {
    return Array.from(document.querySelectorAll('cart-coupon')).filter(
      (el) => el.isConnected
    );
  }

  _mirrorUnavailableUi(code, message) {
    this._getLiveCoupons().forEach((el) => {
      if (el._input && code) el._input.value = code;
      if (message && typeof el._showError === 'function') {
        el._showError(message);
      }
    });
  }

  async _paintUnavailable(code, cartState) {
    const hosts = this._getLiveCoupons();
    const target = (this.isConnected ? this : hosts[0]) || this;
    if (target._input && code) target._input.value = code;
    if (!code) {
      target._showError(this._invalidErrorMessage());
      this._mirrorUnavailableUi('', target._errorEl?.textContent || '');
      return;
    }
    await target._explainAndShow(code, cartState);
    this._mirrorUnavailableUi(code, target._errorEl?.textContent || '');
  }

  _renderSections(parsedState, { skipCouponSlots = false } = {}) {
    if (!parsedState?.sections) return;

    this._getSectionsToRender().forEach((section) => {
      if (skipCouponSlots && this._isCouponSlotSelector(section.selector)) {
        return;
      }
      const sectionHtml = parsedState.sections[section.section];
      if (!sectionHtml) return;

      const container = document.getElementById(section.id);
      if (!container) return;

      const inner = this._getSectionInnerHTML(sectionHtml, section.selector);
      if (inner == null) return;

      const target = container.querySelector(section.selector) || container;
      target.innerHTML = inner;
    });

    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof parsedState.item_count === 'number') {
      cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && typeof parsedState.item_count === 'number') {
      cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper && typeof parsedState.item_count === 'number') {
      cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof parsedState.item_count === 'number') {
      cartDrawerItems.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this._syncVisibilityFromCart(parsedState.item_count);
  }

  async _requestCartUpdate({ discount, sections }) {
    const sectionConfigs = sections || this._getSectionsToRender();
    const payload = {
      sections: sectionConfigs.map((s) => s.section),
      sections_url: window.location.pathname,
    };
    if (typeof discount === 'string') payload.discount = discount;

    const fetchOpts = fetchConfig('json');
    fetchOpts.credentials = 'same-origin';
    fetchOpts.headers = {
      ...fetchOpts.headers,
      'X-Requested-With': 'XMLHttpRequest',
    };
    fetchOpts.body = JSON.stringify(payload);

    const response = await fetch(getCartUpdateJsUrl(), fetchOpts);
    const text = await response.text();

    if (text.trim().startsWith('<') || text.trim().startsWith('<!DOCTYPE')) {
      throw new Error('HTML_RESPONSE');
    }

    const parsedState = JSON.parse(text);
    if (!response.ok || (parsedState.status && parsedState.status >= 400)) {
      const err = new Error('CART_UPDATE_FAILED');
      err.parsedState = parsedState;
      throw err;
    }
    return parsedState;
  }

  async _removeViaDiscountRoute() {
    const root = this._getShopifyRoot();
    const redirect = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    await fetch(`${root}discount/none?redirect=${redirect}`, {
      credentials: 'same-origin',
      redirect: 'follow',
    });
  }

  _broadcastCartState(parsedState) {
    document.querySelectorAll('cart-coupon').forEach((el) => {
      if (el === this) return;
      if (typeof el._syncAppliedFromCartState === 'function') {
        el._syncAppliedFromCartState(parsedState);
      }
    });
  }

  async _showApplyFailure(code, parsedState) {
    this._syncAppliedFromCartState(parsedState);
    this._broadcastCartState(parsedState);
    if (parsedState?.sections) {
      this._renderSections(parsedState, { skipCouponSlots: true });
    }
    await this._paintUnavailable(code, parsedState);
  }

  async _updateDiscountCodes(nextCodes, { submittedCode = null } = {}) {
    const sections = this._getSectionsToRender();
    if (!sections.length) return;

    this._setLoading(true);
    this._clearError();
    this._updateHint();

    const previousCodes = this._appliedItems.map((i) => i.code);
    const discountValue = nextCodes.length
      ? nextCodes.map((c) => String(c).trim()).filter(Boolean).join(',')
      : '';

    try {
      let parsedState = await this._requestCartUpdate({
        discount: discountValue,
        sections,
      });

      if (!discountValue) {
        let cartState = await this._resolveCartState(parsedState);
        if (this._extractAppliedItemsFromCart(cartState).length) {
          await this._removeViaDiscountRoute();
          parsedState = await this._requestCartUpdate({ sections });
          cartState = await this._resolveCartState(parsedState);
        }
        this._clearError();
        this._setAppliedItems([], { clearInput: true });
        this._broadcastCartState(cartState);
        this._renderSections(parsedState);
        return;
      }

      if (parsedState.errors) {
        await this._showApplyFailure(submittedCode, parsedState);
        return;
      }

      let cartState = await this._resolveCartState(parsedState);

      if (submittedCode) {
        const rejected = this._isDiscountCodeRejected(
          cartState,
          submittedCode
        );
        const notOnCart = !this._hasDiscountCodeOnCart(
          cartState,
          submittedCode
        );

        if (rejected || notOnCart) {
          const restored = await this._restorePreviousCodes(
            previousCodes,
            sections
          );
          if (restored) {
            parsedState = restored.parsedState;
            cartState = restored.cartState;
          }
          await this._showApplyFailure(submittedCode, cartState);
          return;
        }

        const missing = this._missingCodesOnCart(cartState, nextCodes);
        if (missing.length) {
          // New code replaced a previous one — restore previous codes.
          if (previousCodes.length) {
            const restored = await this._restorePreviousCodes(
              previousCodes,
              sections
            );
            if (restored) {
              parsedState = restored.parsedState;
              cartState = restored.cartState;
            }
            this._setAppliedItems(
              this._extractAppliedItemsFromCart(cartState, previousCodes)
            );
            this._broadcastCartState(cartState);
            if (parsedState?.sections) {
              this._renderSections(parsedState, { skipCouponSlots: true });
            }
          } else {
            this._syncAppliedFromCartState(cartState);
            this._broadcastCartState(cartState);
            if (parsedState?.sections) {
              this._renderSections(parsedState, { skipCouponSlots: true });
            }
          }
          await this._paintUnavailable(submittedCode, cartState);
          return;
        }
      }

      const items = this._extractAppliedItemsFromCart(cartState, nextCodes);
      this._clearError();
      this._setAppliedItems(items, { clearInput: Boolean(submittedCode) });
      this._broadcastCartState(cartState);
      this._renderSections(parsedState);
    } catch (e) {
      if (e.message === 'HTML_RESPONSE') {
        this._showError(window.cartStrings?.error || 'Error');
        return;
      }
      if (e.message === 'CART_UPDATE_FAILED') {
        await this._showApplyFailure(submittedCode || '', e.parsedState);
        return;
      }
      console.error(e);
      this._showError(window.cartStrings?.error || 'Error');
    } finally {
      this._setLoading(false);
    }
  }

  _onApply() {
    if (this.classList.contains('cart-coupon--at-limit')) return;

    const code = (this._input?.value || '').trim();
    if (!code) {
      this._showError(window.cartStrings?.couponErrorEmpty);
      this._input?.focus();
      return;
    }

    const normalized = this._normalizeCode(code);
    const alreadyApplied = this._appliedItems.some(
      (item) => this._normalizeCode(item.code) === normalized
    );
    if (alreadyApplied) {
      this._showError(
        window.cartStrings?.couponErrorDuplicate ||
          'This coupon code has already been applied'
      );
      return;
    }

    if (this._appliedItems.length >= this._maxCoupons) {
      this._updateLimitUi();
      this._updateHint();
      return;
    }

    const nextCodes = [...this._appliedItems.map((i) => i.code), code];
    this._updateDiscountCodes(nextCodes, { submittedCode: code });
  }

  _onListClick(event) {
    const btn = event.target.closest('.cart-coupon__item-remove');
    if (!btn || !this._listEl?.contains(btn)) return;
    const code = btn.dataset.code || '';
    if (!code) return;
    this._onRemoveCode(code);
  }

  _onRemoveCode(code) {
    const normalized = this._normalizeCode(code);
    const nextCodes = this._appliedItems
      .map((i) => i.code)
      .filter((c) => this._normalizeCode(c) !== normalized);
    this._updateDiscountCodes(nextCodes);
  }
}

if (!customElements.get('cart-coupon')) {
  customElements.define('cart-coupon', CartCoupon);
}

initCartDrawerCouponPositionObserver();
